import { useState, useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'wouter'
import { Card } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import {
	AlertCircle,
	ChevronLeft,
	Users,
	User,
	CalendarDays,
	CreditCard,
	Clock,
	Wallet,
	Mail,
	Phone,
	Loader2
} from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useUser } from '@/hooks/use-user'
import { useGetActiveMemberships } from '@/hooks/use-get-active-memberships'
import AvailableMembershipCard from '@/components/AvailableMembershipCard'
import { EnhancedMembershipTemplate } from '@/components/AvailableMembershipCard'
import { useAvailableMemberships } from '@/hooks/use-available-memberships'
import { usePrices } from '@/hooks/use-prices'
import {
	GuestType,
	MembershipTemplate,
	ICachedMembershipSelection,
	Membership,
	Transaction
} from '@/constants'
import { capitalize, formatDate, formatShortCryptoAddress } from '@/utils/format'
import { apiFetch } from '@/utils'
import { Button } from '@/components/ui/button'
import TransactionDetailDialog from '@/components/TransactionDetailDialog'

interface TaxCalculationResult {
	subtotal: number
	fees: Array<{
		type: string
		rate: number
		amount: number
		description: string
	}>
	total: number
}

const GUEST_TYPE_OPTIONS: { value: GuestType; label: string; icon: React.ReactNode; description: string }[] = [
	{ value: 'couples', label: 'Couples', icon: <Users className="h-5 w-5" />, description: 'Two guests' },
	{ value: 'single_ladies', label: 'Single Ladies', icon: <User className="h-5 w-5" />, description: 'One guest' },
	{ value: 'single_males', label: 'Single Males', icon: <User className="h-5 w-5" />, description: 'One guest' }
]

function getPriceForGuestType(template: MembershipTemplate, guestType: GuestType): number {
	switch (guestType) {
		case 'couples':
			return parseFloat(template.priceCouples) || 0
		case 'single_ladies':
			return parseFloat(template.priceSingleLadies) || 0
		case 'single_males':
			return parseFloat(template.priceSingleMales) || 0
		default:
			return parseFloat(template.priceCouples) || 0
	}
}

export default function MembershipPage() {
	const [isProcessing, setIsProcessing] = useState(false)
	const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
	const [selectedGuestType, setSelectedGuestType] = useState<GuestType>('couples')
	const [error, setError] = useState<string | null>(null)
	const [taxCalculations, setTaxCalculations] = useState<{ [key: string]: TaxCalculationResult }>({})
	const [isCalculatingTaxes, setIsCalculatingTaxes] = useState(false)
	const [, navigate] = useLocation()
	const { toast } = useToast()
	const { uid, walletAddress, profileImageUrl, email, displayName } = useUser()
	const { convertUsdToToken } = usePrices()
	const {
		activeMemberships,
		hasActiveMemberships,
		isLoading: isLoadingMemberships,
		mostRecentActiveMembership
	} = useGetActiveMemberships(walletAddress)

	const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null)
	const [isLoadingTransaction, setIsLoadingTransaction] = useState(false)

	const { templates, isLoading: isLoadingTemplates } =
		useAvailableMemberships()

	// Enhanced templates with SECH pricing and tax calculations
	const templatesWithPricing = useMemo(() => {
		return templates.map(template => {
			const usdPrice = getPriceForGuestType(template, selectedGuestType)
			const sechPrice = convertUsdToToken(usdPrice, 'sech')
			const taxKey = `${template.id}-${selectedGuestType}`
			const taxCalculation = taxCalculations[taxKey]
			const totalSechPrice = taxCalculation ? convertUsdToToken(taxCalculation.total, 'sech') : sechPrice

			return {
				template,
				guestType: selectedGuestType,
				usdPrice,
				sechPrice,
				taxCalculation,
				totalSechPrice
			} as EnhancedMembershipTemplate
		})
	}, [templates, selectedGuestType, convertUsdToToken, taxCalculations])

	// Calculate taxes for all templates when guest type changes
	useEffect(() => {
		if (templates.length === 0) return

		const calculateTaxes = async () => {
			setIsCalculatingTaxes(true)
			const calculations: { [key: string]: TaxCalculationResult } = {}

			try {
				const taxPromises = templates.map(async (template) => {
					const price = getPriceForGuestType(template, selectedGuestType)
					const taxKey = `${template.id}-${selectedGuestType}`

					try {
						const response = await apiFetch('/v1/taxes/calculate', {
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
							},
							body: JSON.stringify({
								amount: price,
								transactionType: 'membership'
							})
						})

						if (!response.ok) {
							throw new Error(`HTTP error! status: ${response.status}`)
						}

						const result = await response.json()
						if (result.success) {
							return { key: taxKey, data: result.data }
						} else {
							throw new Error(result.message || 'Tax calculation failed')
						}
					} catch (error) {
						console.error(`Failed to calculate taxes for template ${template.id}:`, error)
						return {
							key: taxKey,
							data: {
								subtotal: price,
								fees: [],
								total: price
							}
						}
					}
				})

				const results = await Promise.all(taxPromises)

				results.forEach(result => {
					if (result) {
						calculations[result.key] = result.data
					}
				})

			} catch (error) {
				console.error('Error calculating taxes:', error)
				toast({
					title: 'Tax Calculation Error',
					description: 'Unable to calculate taxes. Showing base prices.',
					variant: 'destructive'
				})

				templates.forEach(template => {
					const price = getPriceForGuestType(template, selectedGuestType)
					const taxKey = `${template.id}-${selectedGuestType}`
					calculations[taxKey] = {
						subtotal: price,
						fees: [],
						total: price
					}
				})
			} finally {
				setTaxCalculations(prev => ({ ...prev, ...calculations }))
				setIsCalculatingTaxes(false)
			}
		}

		calculateTaxes()
	}, [templates, selectedGuestType, toast])

	// Purchase handler — stores selection and navigates to waivers
	const handlePurchase = useCallback(async (item: EnhancedMembershipTemplate) => {
		if (isProcessing) return

		try {
			setError(null)
			setIsProcessing(true)

			if (!item.taxCalculation) {
				throw new Error('Tax calculation not available. Please try again.')
			}

			const cacheData: ICachedMembershipSelection = {
				template: item.template,
				guestType: selectedGuestType,
				price: item.usdPrice,
				sechPrice: item.totalSechPrice || item.sechPrice,
				taxCalculation: item.taxCalculation,
				timestamp: Date.now(),
				appliedDiscount: null
			}

			localStorage.setItem('selectedMembership', JSON.stringify(cacheData))

			toast({
				title: 'Membership Selected',
				description: `${item.template.name} selected. Please complete the required waivers.`
			})

			navigate('/waivers')
		} catch (error) {
			const errorMessage =
				error instanceof Error
					? error.message
					: 'Failed to process membership selection'
			setError(errorMessage)
			toast({
				title: 'Selection Failed',
				description: errorMessage,
				variant: 'destructive'
			})
			localStorage.removeItem('selectedMembership')
			setSelectedTemplateId(null)
		} finally {
			setIsProcessing(false)
		}
	}, [isProcessing, navigate, toast, selectedGuestType])

	useEffect(() => {
		!walletAddress && selectedTemplateId && setSelectedTemplateId(null)
	}, [walletAddress, selectedTemplateId])

	const handleMembershipSelection = (item: EnhancedMembershipTemplate) => {
		// Don't allow selection if taxes are still calculating
		if (isCalculatingTaxes || !item.taxCalculation) {
			return
		}

		setSelectedTemplateId(item.template.id)
		handlePurchase(item)
	}

	const canBuyMembershipToday = useMemo(() => {
		if (!hasActiveMemberships || !mostRecentActiveMembership) return true
		const now = Date.now()
		const membershipStartDate = new Date(
			mostRecentActiveMembership.startDate
		).getTime()
		const minAllowedDateToBuy = membershipStartDate + 24 * 60 * 60 * 1000

		return now >= minAllowedDateToBuy
	}, [hasActiveMemberships, mostRecentActiveMembership])

	const formatGuestTypeLabel = (guestType: GuestType | null | undefined): string => {
		if (!guestType) return 'N/A'
		const labels: Record<GuestType, string> = {
			couples: 'Couples',
			single_ladies: 'Single Ladies',
			single_males: 'Single Males',
		}
		return labels[guestType] || guestType
	}

	const formatDuration = (months: number | null | undefined): string => {
		if (!months) return 'N/A'
		if (months >= 12) {
			const years = months / 12
			return years === 1 ? '1 Year' : `${years} Years`
		}
		return months === 1 ? '1 Month' : `${months} Months`
	}

	const formatActiveMembershipName = (ms: Membership) => {
		if (ms.templateName) return ms.templateName
		if (ms.type) return capitalize(ms.type)
		return 'Membership'
	}

	const handleTransactionClick = useCallback(async (transactionId: number) => {
		if (!uid || isLoadingTransaction) return
		setIsLoadingTransaction(true)
		try {
			const res = await apiFetch(`/v1/transactions/${uid}`, { withCredentials: true })
			if (!res.ok) throw new Error('Failed to fetch transactions')
			const json = await res.json()
			const transactions: Transaction[] = json?.data?.items ?? json
			const match = transactions.find((tx) => tx.id === transactionId)
			if (match) {
				setSelectedTransaction(match)
			} else {
				toast({ title: 'Not Found', description: 'Transaction not found.', variant: 'destructive' })
			}
		} catch {
			toast({ title: 'Error', description: 'Failed to load transaction details.', variant: 'destructive' })
		} finally {
			setIsLoadingTransaction(false)
		}
	}, [uid, isLoadingTransaction, toast])

	return (
		<div className="min-h-auto bg-[#18181B]">
			<div className="max-w-5xl mx-auto px-5 pb-8">
				<div
					role="banner"
					className="sticky top-0 z-50 bg-[#18181B] pb-[12px] backdrop-blur-[8px] mb-[12px] h-auto flex items-center justify-start gap-2 text-white py-3"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/')}
						className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className='h-[22px] w-[22px] text-[#ffffff]'/>
					</button>

					<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
						Membership
					</h1>
				</div>

					{isLoadingMemberships ? (
					<div className="flex justify-center items-center py-20">
						<Loader2 className="h-8 w-8 animate-spin text-[#BABABB]" />
					</div>
				) : hasActiveMemberships ? (
					<>
						<div className="text-start mb-6">
							<h1 className="text-[16px] leading-[18px] font-semibold font-figtree text-[#ffffff] mb-2">Your Membership</h1>
							<p className="text-[13px] leading-[15px] font-normal text-[#BABABB]">
								Your current membership details.
							</p>
						</div>

						<div className="flex flex-col gap-4">
							{activeMemberships.filter(ms => ms.status === 'active').map((ms) => (
								<Card key={ms.id} className="p-1 bg-[#3C3C3C] rounded-[16px] border-0">
									<div className="bg-[linear-gradient(180.06deg,_#2F2F32_0.05%,_#2A2A2C_99.95%)] rounded-[12px] p-5 relative overflow-hidden">

										<div className="bg-[#709EFF] h-[220px] w-[220px] rounded-full blur-[37px] absolute z-[2] top-[-110px] left-1/2 -translate-x-1/2 opacity-50"></div>
										<div className="bg-[#2F2F32] h-[220px] w-[220px] rounded-full absolute left-1/2 -translate-x-1/2 top-[60px] z-[2] blur-[70px]"></div>

										<div className="relative z-[5]">
											{/* Profile photo + status badge + plan name */}
											<div className="flex items-start justify-between mb-4">
												<div className="flex items-center gap-3">
													{profileImageUrl ? (
														<img
															src={profileImageUrl}
															alt="Profile"
															className="h-14 w-14 rounded-full object-cover ring-2 ring-white/20 shrink-0"
														/>
													) : (
														<div className="h-14 w-14 rounded-full bg-white/10 flex items-center justify-center ring-2 ring-white/20 shrink-0">
															<User className="h-6 w-6 text-[#BABABB]" />
														</div>
													)}
													<div>
														{displayName && (
															<p className="text-[13px] text-[#BABABB] font-figtree">{displayName}</p>
														)}
														<h2 className="text-[28px] leading-[32px] font-bold text-[#ffffff] font-figtree">
															{formatActiveMembershipName(ms)}
														</h2>
														<span className="text-[13px] text-[#BABABB] font-figtree">
															Membership #{ms.id}
															{ms.transactionId && (
																<>
																	{' '}&middot;{' '}
																	<button
																		type="button"
																		onClick={() => handleTransactionClick(ms.transactionId!)}
																		disabled={isLoadingTransaction}
																		className="text-[#709EFF] hover:text-[#93b4ff] underline underline-offset-2 transition-colors inline-flex items-center gap-1"
																	>
																		Transaction #{ms.transactionId}
																		{isLoadingTransaction && <Loader2 className="h-3 w-3 animate-spin inline" />}
																	</button>
																</>
															)}
														</span>
													</div>
												</div>
												<span className="text-xs font-semibold bg-green-500/20 text-green-500 px-3 py-1 rounded-full shrink-0">
													Active
												</span>
											</div>

											{/* Details grid */}
											<div className="grid grid-cols-2 gap-4 mt-4">
												{/* Guest Type */}
												<div className="flex items-center gap-2">
													{ms.guestType === 'couples'
														? <Users className="h-4 w-4 text-[#BABABB] shrink-0" />
														: <User className="h-4 w-4 text-[#BABABB] shrink-0" />
													}
													<div>
														<p className="text-[11px] text-[#808080] font-figtree uppercase tracking-wide">Guest Type</p>
														<p className="text-[14px] text-[#ffffff] font-medium font-figtree">{formatGuestTypeLabel(ms.guestType)}</p>
													</div>
												</div>

												{/* Duration */}
												<div className="flex items-center gap-2">
													<Clock className="h-4 w-4 text-[#BABABB] shrink-0" />
													<div>
														<p className="text-[11px] text-[#808080] font-figtree uppercase tracking-wide">Duration</p>
														<p className="text-[14px] text-[#ffffff] font-medium font-figtree">{formatDuration(ms.durationMonths)}</p>
													</div>
												</div>

												{/* Period */}
												<div className="flex items-center gap-2">
													<CalendarDays className="h-4 w-4 text-[#BABABB] shrink-0" />
													<div>
														<p className="text-[11px] text-[#808080] font-figtree uppercase tracking-wide">Valid Period</p>
														<p className="text-[14px] text-[#ffffff] font-medium font-figtree">{formatDate(ms.startDate)} — {formatDate(ms.endDate)}</p>
													</div>
												</div>

												{/* Price */}
												{ms.price && (
													<div className="flex items-center gap-2">
														<CreditCard className="h-4 w-4 text-[#BABABB] shrink-0" />
														<div>
															<p className="text-[11px] text-[#808080] font-figtree uppercase tracking-wide">Price Paid</p>
															<p className="text-[14px] text-[#ffffff] font-medium font-figtree">${Number(ms.price).toFixed(2)}</p>
														</div>
													</div>
												)}

												{/* Primary Name */}
												<div className="flex items-center gap-2">
													<User className="h-4 w-4 text-[#BABABB] shrink-0" />
													<div>
														<p className="text-[11px] text-[#808080] font-figtree uppercase tracking-wide">Primary</p>
														<p className="text-[14px] text-[#ffffff] font-medium font-figtree">{ms.primaryName || 'N/A'}</p>
													</div>
												</div>

												{/* Partner Name */}
												<div className="flex items-center gap-2">
													<Users className="h-4 w-4 text-[#BABABB] shrink-0" />
													<div>
														<p className="text-[11px] text-[#808080] font-figtree uppercase tracking-wide">Partner</p>
														<p className="text-[14px] text-[#ffffff] font-medium font-figtree">{ms.partnerName || 'N/A'}</p>
													</div>
												</div>

												{/* Wallet */}
												{walletAddress && (
													<div className="flex items-center gap-2">
														<Wallet className="h-4 w-4 text-[#BABABB] shrink-0" />
														<div>
															<p className="text-[11px] text-[#808080] font-figtree uppercase tracking-wide">Wallet</p>
															<p className="text-[14px] text-[#ffffff] font-medium font-figtree">{formatShortCryptoAddress(walletAddress)}</p>
														</div>
													</div>
												)}

												{/* Email */}
												{email && (
													<div className="flex items-center gap-2">
														<Mail className="h-4 w-4 text-[#BABABB] shrink-0" />
														<div>
															<p className="text-[11px] text-[#808080] font-figtree uppercase tracking-wide">Email</p>
															<p className="text-[14px] text-[#ffffff] font-medium font-figtree">{email}</p>
														</div>
													</div>
												)}

												{/* Phone */}
												{ms.phoneNumber && (
													<div className="flex items-center gap-2">
														<Phone className="h-4 w-4 text-[#BABABB] shrink-0" />
														<div>
															<p className="text-[11px] text-[#808080] font-figtree uppercase tracking-wide">Phone</p>
															<p className="text-[14px] text-[#ffffff] font-medium font-figtree">{ms.phoneNumber}</p>
														</div>
													</div>
												)}
											</div>
										</div>
									</div>
								</Card>
							))}
						</div>
					</>
				) : (
					<>
						<div className="text-start mb-4">
							<h1 className="text-[16px] leading-[18px] font-semibold font-figtree text-[#ffffff] mb-2">Choose Your Membership</h1>
							<p className="text-[13px] leading-[15px] font-normal text-[#BABABB]">
								Select your guest type and choose a membership plan. All prices include applicable taxes.
							</p>
							{isCalculatingTaxes && (
								<p className="text-primary mt-2 flex justify-center items-center gap-2">
									<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
									Calculating taxes for all memberships...
								</p>
							)}
						</div>

						{/* Guest Type Selection */}
						<div className="mb-6">
							<h2 className="text-[14px] leading-[16px] font-semibold font-figtree text-[#ffffff] mb-3">
								Select Guest Type
							</h2>
							<div className="grid grid-cols-3 gap-3">
								{GUEST_TYPE_OPTIONS.map((option) => (
									<Button
										key={option.value}
										variant={selectedGuestType === option.value ? 'default' : 'outline'}
										className={`h-auto py-3 px-4 flex flex-col items-center gap-1 ${
											selectedGuestType === option.value
												? 'bg-primary text-primary-foreground'
												: 'bg-[#3C3C3C] border-0 text-white hover:bg-[#4C4C4C]'
										}`}
										onClick={() => {
											setSelectedGuestType(option.value)
											setSelectedTemplateId(null)
										}}
									>
										{option.icon}
										<span className="text-xs font-medium">{option.label}</span>
									</Button>
								))}
							</div>
						</div>

						{error && (
							<Alert variant="destructive" className="mb-6">
								<AlertCircle className="h-4 w-4" />
								<AlertDescription>{error}</AlertDescription>
							</Alert>
						)}

						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
							{templatesWithPricing.map((item) => (
								<AvailableMembershipCard
									key={item.template.id}
									onSelect={handleMembershipSelection}
									isCurrent={false}
									isSelected={selectedTemplateId === item.template.id}
									canBuyToday={canBuyMembershipToday && !isCalculatingTaxes}
									membership={item}
									isLoading={isCalculatingTaxes || !item.taxCalculation}
								/>
							))}
						</div>
					</>
				)}
			</div>

			<TransactionDetailDialog
				transaction={selectedTransaction}
				open={selectedTransaction !== null}
				onOpenChange={(open) => { if (!open) setSelectedTransaction(null) }}
			/>
		</div>
	)
}
